<template>
  <section class="steps-main">
    <div class="steps-main__bg"></div>
    <div class="steps-main__content">
      <div class="step-head">
        <div class="container">
          <div class="head-row">
            <div class="head-row__position">
              <i18n path="forms.formHeader" tag="h2">
                <span>{{ this.$i18n.t("address.step") }}</span>
              </i18n>
            </div>
            <h1>{{ this.$i18n.t("address.stepDescription") }}</h1>
            <img src="@/assets/head-paso2.svg" class="head-row__img" />
          </div>
        </div>
      </div>
      <div class="container">
        <div class="box-form box-form--step2">
          <v-row>
            <v-spacer></v-spacer>
            <v-col cols="12" xs="12" sm="12" md="12" lg="10" xl="10">
              <v-form ref="form" v-model="valid" @submit.prevent="saveAddress">
                <div class="row-form">
                  <div class="col-form">
                    <div class="col-form__item">
                      <label>{{ this.$i18n.t("address.phone") }}</label>
                      <vue-tel-input id="address-phone" v-model="phone"
                        :placeholder="this.$i18n.t('address.phonePlaceholder')" mode="international" autocomplete="off"
                        :enabledCountryCode="true" :validCharactersOnly="true" required @validate="validatePhone"
                        defaultCountry="CL"
                        class="v-input v-text-field v-input--is-label-active v-input--is-dirty theme--light phone-field" />
                    </div>
                  </div>
                  <div class="col-form">
                    <div class="col-form__item">
                      <label>{{ this.$i18n.t("address.street") }}</label>
                      <vuetify-google-autocomplete placeholder id="streetsearch" ref="streetsearch"
                        @placechanged="getAddressData" @no-results-found="setStreet" @blur="setStreet" :placeName="true"
                        country="cl" :hint="this.$i18n.t('hints.address')" persistent-hint />
                    </div>
                  </div>
                  <div class="col-form">
                    <div class="col-form__item">
                      <label>{{ this.$i18n.t("address.streetNumber") }}</label>
                      <v-text-field id="address-number" v-model="street_number" :rules="numberRules" required
                        autocomplete="off" />
                    </div>
                  </div>
                  <div class="col-form">
                    <div class="col-form__item">
                      <label>{{ this.$i18n.t("address.locality") }}</label>
                      <v-text-field id="address-locality" v-model="locality" :rules="addressRules" required
                        autocomplete="off" />
                    </div>
                  </div>
                  <div class="col-form">
                    <div class="col-form__item">
                      <label>{{ this.$i18n.t("address.region") }}</label>
                      <v-autocomplete v-model="region" :rules="regionRules" :items="regions" item-text="name"
                        item-value="code" required id="address-region"></v-autocomplete>
                    </div>
                  </div>
                  <div class="col-form">
                    <div class="col-form__item">
                      <label>{{ this.$i18n.t("address.country") }}</label>
                      <v-autocomplete v-model="country" :rules="countryRules" :items="countries" item-text="country"
                        item-value="code" required id="address-country"></v-autocomplete>
                    </div>
                  </div>
                  <div class="col-form">
                    <div class="col-form__item">
                      <label>{{ this.$i18n.t("address.houseType") }}</label>
                      <v-autocomplete id="address-house-type" v-model="house_type" :items="houses" :rules="houseTypeRules"
                        item-text="type" item-value="code" required></v-autocomplete>
                    </div>
                  </div>
                  <div class="col-form">
                    <div class="col-form__item" v-if="house_type && house_type !== 1">
                      <label>{{ this.$i18n.t("address.innerNumber") }}</label>
                      <v-text-field id="address-interior-number" v-model="interior_number" :rules="addressRules" required
                        autocomplete="off"></v-text-field>
                    </div>
                  </div>
                </div>
                <div class="btns-nopad">
                  <v-btn class="btn-finish" color="primary" :loading="loading"
                    :disabled="!valid || loading || !street || !phoneValid" type="submit">{{
                      this.$i18n.t("address.buttonContinue") }}</v-btn>
                </div>
              </v-form>
            </v-col>
            <v-spacer></v-spacer>
          </v-row>
        </div>
      </div>
      <v-dialog v-model="savingDialog" persistent width="300">
        <v-card color="primary" dark>
          <div class="ma-2">
            <v-card-text>
              <p>{{ this.$i18n.t("user.savingDialog") }}</p>
              <v-progress-linear indeterminate color="white"></v-progress-linear>
            </v-card-text>
          </div>
        </v-card>
      </v-dialog>
      <v-dialog v-model="errorDialog" width="300">
        <v-card>
          <v-card-text>
            <div class="box-photo text-alert">
              <v-icon color="primary">mdi-alert-circle-outline</v-icon>
              <p>{{ errorMessage }}</p>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="errorDialog = false">{{ this.$i18n.t("errors.button") }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </section>
</template>

<script>
/* eslint-disable arrow-parens, operator-linebreak, implicit-arrow-linebreak */
import { addressSelects } from "@/mixins/addressSelects";
import { formSelects } from "@/mixins/formSelects";

export default {
  name: "AddressForm",
  mixins: [addressSelects, formSelects],
  data() {
    return {
      allowChange: true,
      errorDialog: false,
      errorMessage: this.$i18n.t("errors.default"),
      savingDialog: false,
      valid: false,
      phoneValid: false,
      loading: false,
      phone: null,
      address: null,
      street: null,
      region: null,
      locality: null,
      street_number: null,
      country: null,
      house_type: 1,
      interior_number: null,
      addressRules: [v => !!v || this.$i18n.t("forms.formMustComplete")],
      regionRules: [v => !!v || this.$i18n.t("forms.formMustComplete"), v => this.regions.some(el => el.code === v) || this.$i18n.t("forms.formGeneric")],
      countryRules: [v => !!v || this.$i18n.t("forms.formMustComplete"), v => this.countries.some(el => el.code === v) || this.$i18n.t("forms.formCountry")],
      numberRules: [v => !!v || this.$i18n.t("forms.formMustComplete"), v => /^[-+]?\d*$/.test(v) || this.$i18n.t("forms.formNumber")],
      houseTypeRules: [v => !!v || this.$i18n.t("forms.formMustComplete"), v => this.houses.some(el => el.code === v) || this.$i18n.t("forms.formGeneric")],
      phoneRules: [v => !!v || this.$i18n.t("forms.formMustComplete")],
    };
  },
  methods: {
    saveAddress() {
      this.loading = true;
      this.savingDialog = true;
      this.$store.commit("updateContactData", {
        phone: this.phone.replace(/\s/g, ""),
        street: this.street,
        region: this.region,
        locality: this.locality,
        street_number: this.street_number,
        country: this.country,
        house_type: this.house_type,
        interior_number: this.house_type !== 1 ? this.interior_number : null,
      });
      this.$store.commit("updateFormStep", { step: 3 });
      this.savingDialog = false;
      this.loading = false;
      this.$router.push("profiling");
    },
    getAddressData(addressData) {
      if (addressData) {
        this.allowChange = false;
        this.street = addressData.route;
        this.street_number = addressData.street_number;
        this.region = this.regions.filter(x => x.name === addressData.administrative_area_level_1)[0].code;
        this.locality = addressData.locality;
        this.country = this.countries.filter(x => x.country === addressData.country)[0].code;
      }
    },
    setStreet() {
      if (this.allowChange) {
        const elem = document.getElementById("streetsearch");
        if (elem && elem.value) {
          this.street = elem.value;
        }
      } else {
        this.allowChange = true;
      }
    },
    validatePhone(values) {
      this.phoneValid = values.valid;
    },
  },
  mounted() { },
};
</script>

<style scoped>
.phone-field {
  background-color: transparent !important;
  border: none !important;
  border-bottom: 1px solid #a0a0a0 !important;
  border-radius: 0px !important;
}
</style>
